import React, { Dispatch } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  CreativeImportIssue,
  CreativeImportRowReport,
  CREATIVE_IMPORT_LIMITS,
} from '@marketing-milk/interfaces'
import { CreativeImportAction, CreativeImportState } from '../state'

interface Props {
  state: CreativeImportState
  dispatch: Dispatch<CreativeImportAction>
  onStartOver: () => void
}

function IssueChips({
  issues,
  color,
}: {
  issues: CreativeImportIssue[]
  color: 'error' | 'warning'
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {issues.map((issue, i) => (
        <Tooltip key={`${issue.code}-${i}`} title={issue.message}>
          <Chip
            size="small"
            color={color}
            variant="outlined"
            label={issue.code.replace(/_/g, ' ')}
          />
        </Tooltip>
      ))}
    </div>
  )
}

function rowStatus(row: CreativeImportRowReport): React.ReactNode {
  if (!row.selectable) return <IssueChips issues={row.hardErrors} color="error" />
  const hasCopy = row.row.headline || row.row.description || row.row.primary_text
  return (
    <div className="flex flex-col gap-1">
      {!hasCopy && (
        <span className="text-gray-500 text-xs">Incomplete draft — copy added in Builder</span>
      )}
      <IssueChips issues={row.warnings} color="warning" />
    </div>
  )
}

export const ReviewStep = ({ state, dispatch, onStartOver }: Props) => {
  const report = state.report
  if (!report) return null

  const selectedCount = report.rows.filter(r => state.rows[r.rowNumber]?.selected).length

  return (
    <div className="flex flex-col gap-4">
      {report.archiveWarnings.map(warning => (
        <Alert key={warning.code} severity="warning">
          {warning.message}
        </Alert>
      ))}

      <TableContainer className="max-h-96">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Image</TableCell>
              <TableCell>Creative name</TableCell>
              <TableCell>Image path</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.rows.map(row => {
              const ui = state.rows[row.rowNumber]
              const thumbnail = state.thumbnails[row.rowNumber]
              return (
                <TableRow key={row.rowNumber} className={row.selectable ? '' : 'opacity-60'}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={ui?.selected ?? false}
                      disabled={!row.selectable}
                      onChange={() => dispatch({ type: 'toggleRow', rowNumber: row.rowNumber })}
                      inputProps={{ 'aria-label': `select row ${row.rowNumber}` }}
                    />
                  </TableCell>
                  <TableCell>
                    {thumbnail ? (
                      <img
                        src={thumbnail}
                        alt={row.effectiveName}
                        className="w-16 h-16 object-cover rounded"
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded" />
                    )}
                  </TableCell>
                  <TableCell className="min-w-[220px]">
                    <TextField
                      size="small"
                      fullWidth
                      value={ui?.editedName ?? row.effectiveName}
                      disabled={!row.selectable}
                      inputProps={{ maxLength: CREATIVE_IMPORT_LIMITS.creativeNameMaxLength }}
                      onChange={e =>
                        dispatch({
                          type: 'editName',
                          rowNumber: row.rowNumber,
                          name: e.target.value,
                        })
                      }
                    />
                  </TableCell>
                  <TableCell className="text-gray-600">{row.row.image_path ?? '—'}</TableCell>
                  <TableCell>{rowStatus(row)}</TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {selectedCount} of {report.counts.totalRows} rows selected
          {report.counts.blockedRows > 0 && ` · ${report.counts.blockedRows} blocked`}
          {report.counts.orphanImages > 0 &&
            ` · ${report.counts.orphanImages} unreferenced images ignored`}
        </p>
        <div className="flex gap-2">
          <Button onClick={onStartOver}>Start Over</Button>
          <Button
            variant="contained"
            disabled={selectedCount === 0}
            onClick={() => dispatch({ type: 'goToOptions' })}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  )
}
